import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

//CSS
import "./tutorialOverlay.css";

//Components
import Button from "./button";

//Functions and hooks
import { useTutorial } from "../../Contexts/TutorialContext";

const SPOTLIGHT_PADDING = 10;
const TOOLTIP_MARGIN = 18;

export default function TutorialOverlay() {
  const { active, advanceStep, endTutorial } = useTutorial();
  const { t } = useTranslation();

  const [targetBox, setTargetBox] = useState(null);
  const [tooltipStyle, setTooltipStyle] = useState({ top: 0, left: 0 });
  const [confirmingExit, setConfirmingExit] = useState(false);
  const tooltipRef = useRef(null);

  const step = active ? active.steps[active.stepIndex] : null;

  // Continuously track the target element's position (handles scrolling,
  // resizing, and content shifting) and re-derive the tooltip placement
  // from it every frame - much simpler and more robust than wiring up
  // separate scroll/resize listeners.
  useEffect(() => {
    if (!active || !step) return;
    let rafId;
    let scrolledIntoView = false;

    const measure = () => {
      // A target can be a data-tutorial-id key (the common case - stable,
      // added specifically for the tour) or, for spots inside complex
      // shared components we don't want to modify just for this, a raw
      // CSS selector (starts with . # or [) resolved as-is.
      const el = step.target
        ? /^[.#[]/.test(step.target)
          ? document.querySelector(step.target)
          : document.querySelector(`[data-tutorial-id="${step.target}"]`)
        : null;

      if (el) {
        if (!scrolledIntoView) {
          el.scrollIntoView({ block: "center", behavior: "smooth" });
          scrolledIntoView = true;
        }
        // display:contents targets (bare tutorial-wrapper divs, see
        // global.css) report a zero-size rect for themselves - fall back to
        // the union of their children's rects, which is what's actually
        // visible on screen.
        let r = el.getBoundingClientRect();
        if (r.width === 0 && r.height === 0 && el.children.length > 0) {
          let top = Infinity,
            left = Infinity,
            right = -Infinity,
            bottom = -Infinity;
          for (const child of el.children) {
            const cr = child.getBoundingClientRect();
            if (cr.width === 0 && cr.height === 0) continue;
            top = Math.min(top, cr.top);
            left = Math.min(left, cr.left);
            right = Math.max(right, cr.right);
            bottom = Math.max(bottom, cr.bottom);
          }
          if (right > left && bottom > top) {
            r = { top, left, width: right - left, height: bottom - top };
          }
        }
        const box = {
          top: r.top - SPOTLIGHT_PADDING,
          left: r.left - SPOTLIGHT_PADDING,
          width: r.width + SPOTLIGHT_PADDING * 2,
          height: r.height + SPOTLIGHT_PADDING * 2,
        };
        setTargetBox(box);

        const tooltipEl = tooltipRef.current;
        const tw = tooltipEl?.offsetWidth ?? 340;
        const th = tooltipEl?.offsetHeight ?? 140;
        const vw = window.innerWidth;
        const vh = window.innerHeight;

        const spaceBelow = vh - (box.top + box.height);
        const placeBelow = spaceBelow >= th + TOOLTIP_MARGIN || spaceBelow >= box.top;

        let top = placeBelow
          ? box.top + box.height + TOOLTIP_MARGIN
          : box.top - th - TOOLTIP_MARGIN;
        top = Math.max(TOOLTIP_MARGIN, Math.min(top, vh - th - TOOLTIP_MARGIN));

        let left = box.left + box.width / 2 - tw / 2;
        left = Math.max(TOOLTIP_MARGIN, Math.min(left, vw - tw - TOOLTIP_MARGIN));

        setTooltipStyle({ top, left });
      } else {
        setTargetBox(null);
        const tooltipEl = tooltipRef.current;
        const tw = tooltipEl?.offsetWidth ?? 340;
        const th = tooltipEl?.offsetHeight ?? 140;
        setTooltipStyle({
          top: (window.innerHeight - th) / 2,
          left: (window.innerWidth - tw) / 2,
        });
      }

      rafId = requestAnimationFrame(measure);
    };

    rafId = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(rafId);
  }, [active, step]);

  useEffect(() => {
    setConfirmingExit(false);
  }, [active?.stepIndex]);

  if (!active || !step) return null;

  const isLastStep = active.stepIndex === active.steps.length - 1;

  const handleAdvance = () => {
    if (confirmingExit) return;
    advanceStep();
  };

  const handleCloseClick = (e) => {
    e.stopPropagation();
    if (!active.isReplay) {
      setConfirmingExit(true);
    } else {
      endTutorial(true);
    }
  };

  return (
    <div
      className="tutorial-click-catcher"
      onClick={handleAdvance}
      role="presentation"
    >
      {targetBox ? (
        // A single transparent box whose oversized box-shadow doubles as
        // both the full-screen dim AND the cutout "hole" around it - no
        // separate dim layer needed while a target is spotlighted.
        <div
          className="tutorial-spotlight"
          style={{
            top: targetBox.top,
            left: targetBox.left,
            width: targetBox.width,
            height: targetBox.height,
          }}
        />
      ) : (
        <div className="tutorial-dim" />
      )}
      <div
        ref={tooltipRef}
        className="tutorial-tooltip"
        style={{ top: tooltipStyle.top, left: tooltipStyle.left }}
      >
        <button
          className="tutorial-close-btn"
          onClick={handleCloseClick}
          aria-label={t("Close")}
        >
          &times;
        </button>

        {!confirmingExit ? (
          <>
            {step.title && <p className="tutorial-title">{step.title}</p>}
            <p className="tutorial-message">{step.message}</p>
            <div className="tutorial-footer">
              <span className="tutorial-progress">
                {t("Step {{current}} of {{total}}", {
                  current: active.stepIndex + 1,
                  total: active.steps.length,
                })}
              </span>
              <Button
                btnMargin="btn-default"
                btnStyle="btn-positive"
                btnSize="btn-small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleAdvance();
                }}
              >
                {isLastStep ? t("Finish") : t("Next")}
              </Button>
            </div>
            <p className="tutorial-hint">
              {t("Click anywhere to continue")}
            </p>
          </>
        ) : (
          <div className="tutorial-confirm-exit" onClick={(e) => e.stopPropagation()}>
            <p className="tutorial-message">
              {t(
                "You're seeing this tutorial for the first time - we'd really recommend finishing it so you know how this page works."
              )}
            </p>
            <div className="tutorial-footer center">
              <Button
                btnMargin="btn-default"
                btnStyle="btn-positive"
                btnSize="btn-small"
                onClick={() => setConfirmingExit(false)}
              >
                {t("Keep watching")}
              </Button>
              <Button
                btnMargin="btn-default"
                btnStyle="btn-negative"
                btnSize="btn-small"
                onClick={() => endTutorial(true)}
              >
                {t("Skip anyway")}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
